import { useEffect, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import { axiosCaller } from '../../utils/apiCaller';
import { toast } from 'react-toastify';
import { t } from '../../i18n';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import MikaTranslationsModal from '../../components/MikaTranslationsModal/MikaTranslationsModal';
import './offerSettings.scss';

interface BlockType {
    id: number;
    name: string;
    is_default: boolean;
    sort_order: number;
}

interface BlockCategory {
    id: number;
    name: string;
    name_translations?: Record<string, string>;
    is_default: boolean;
    is_protected: boolean;
    sort_order: number;
    types: BlockType[];
}

interface Props {
    actions: any;
    buttonLoader: boolean;
}

const API_BASE = 'admin/installation-block-categories';

// Color palette per category name
var categoryColors: Record<string, { border: string; bg: string; icon: string }> = {
    'Materiale': { border: '#3b82f6', bg: '#dbeafe', icon: '#2563eb' },
    'Klienti': { border: '#8b5cf6', bg: '#ede9fe', icon: '#7c3aed' },
    'Aksesi': { border: '#f59e0b', bg: '#fef3c7', icon: '#d97706' },
    'Teknike': { border: '#0891b2', bg: '#cffafe', icon: '#0e7490' },
    'Tjeter': { border: '#94a3b8', bg: '#f1f5f9', icon: '#64748b' },
};
var defaultColor = { border: '#1e3a5f', bg: '#eef3f9', icon: '#1e3a5f' };

var getCatColor = function(name: string) {
    return categoryColors[name] || defaultColor;
};

// Simple category icons
var categoryIcons: Record<string, JSX.Element> = {
    'Materiale': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
    'Klienti': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    'Aksesi': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    'Teknike': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
    'Tjeter': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>,
};
var defaultIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>;

var btnStyle = function(hoverBg: string): React.CSSProperties {
    return { background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' };
};

var inputStyle: React.CSSProperties = {
    flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13, outline: 'none',
};

var InstallationBlockCategories = function(props: Props) {
    var intl = useIntl();
    var fm = function(id: string, defaultMessage: string) { return intl.formatMessage({ id: id, defaultMessage: defaultMessage }); };
    var _s = useState<BlockCategory[]>([]);
    var categories = _s[0]; var setCategories = _s[1];
    var _l = useState(true);
    var loading = _l[0]; var setLoading = _l[1];
    var _e = useState<number | null>(null);
    var expandedId = _e[0]; var setExpandedId = _e[1];

    var _nc = useState('');
    var newCatName = _nc[0]; var setNewCatName = _nc[1];
    var _ec = useState<number | null>(null);
    var editingCatId = _ec[0]; var setEditingCatId = _ec[1];
    var _en = useState('');
    var editingCatName = _en[0]; var setEditingCatName = _en[1];

    var _nt = useState('');
    var newTypeName = _nt[0]; var setNewTypeName = _nt[1];
    var _et = useState<number | null>(null);
    var editingTypeId = _et[0]; var setEditingTypeId = _et[1];
    var _etn = useState('');
    var editingTypeName = _etn[0]; var setEditingTypeName = _etn[1];

    var _dc = useState<{ type: 'category' | 'type'; catId: number; typeId?: number; name: string } | null>(null);
    var deleteConfirm = _dc[0]; var setDeleteConfirm = _dc[1];

    // Translation modal: non-null when editing the per-locale name map of a category.
    var _tr = useState<BlockCategory | null>(null);
    var translatingCategory = _tr[0]; var setTranslatingCategory = _tr[1];

    useEffect(function() { loadCategories(); }, []);

    var loadCategories = function() {
        setLoading(true);
        axiosCaller('GET', 'installation-block-categories').then(function(res: any) {
            setCategories(res?.data || []);
            setLoading(false);
        }).catch(function() { setLoading(false); });
    };

    var addCategory = function() {
        if (!newCatName.trim()) { toast.error(t('admin.installationBlockCategories.toastFillCatName')); return; }
        axiosCaller('POST', API_BASE, { name: newCatName.trim() }).then(function() {
            toast.success(t('admin.installationBlockCategories.toastCatAdded'));
            setNewCatName('');
            loadCategories();
        }).catch(function() { toast.error(t('admin.toast.somethingWentWrong')); });
    };

    var saveCatEdit = function() {
        if (!editingCatName.trim()) { toast.error(t('admin.installationBlockCategories.toastNameEmpty')); return; }
        axiosCaller('PATCH', API_BASE + '/' + editingCatId, { name: editingCatName.trim() }).then(function() {
            toast.success(t('admin.installationBlockCategories.toastCatUpdated'));
            setEditingCatId(null); setEditingCatName('');
            loadCategories();
        }).catch(function() { toast.error(t('admin.toast.somethingWentWrong')); });
    };

    var deleteCategory = function(cat: BlockCategory) {
        if (cat.is_protected) { toast.error(t('admin.installationBlockCategories.toastCatProtected')); return; }
        setDeleteConfirm({ type: 'category', catId: cat.id, name: cat.name });
    };

    var saveTranslations = async function(translations: Record<string, string>) {
        if (!translatingCategory) return;
        try {
            await axiosCaller(
                'PUT',
                API_BASE + '/' + translatingCategory.id + '/translations',
                { translations: translations }
            );
            toast.success(intl.formatMessage({
                id: 'admin.installationBlockCategories.toastTranslationsSaved',
                defaultMessage: 'Përkthimet u ruajtën',
            }));
            setTranslatingCategory(null);
            loadCategories();
        } catch (e) {
            toast.error(t('admin.toast.somethingWentWrong'));
        }
    };

    var addType = function(categoryId: number) {
        if (!newTypeName.trim()) { toast.error(t('admin.installationBlockCategories.toastFillTypeName')); return; }
        axiosCaller('POST', API_BASE + '/' + categoryId + '/types', { name: newTypeName.trim() }).then(function() {
            toast.success(t('admin.installationBlockCategories.toastTypeAdded'));
            setNewTypeName('');
            loadCategories();
        }).catch(function(err: any) {
            toast.error(err?.response?.data?.message || t('admin.toast.somethingWentWrong'));
        });
    };

    var saveTypeEdit = function(categoryId: number) {
        if (!editingTypeName.trim()) { toast.error(t('admin.installationBlockCategories.toastNameEmpty')); return; }
        axiosCaller('PATCH', API_BASE + '/' + categoryId + '/types/' + editingTypeId, { name: editingTypeName.trim() }).then(function() {
            toast.success(t('admin.installationBlockCategories.toastTypeUpdated'));
            setEditingTypeId(null); setEditingTypeName('');
            loadCategories();
        }).catch(function(err: any) { toast.error(err?.response?.data?.message || t('admin.toast.somethingWentWrong')); });
    };

    var deleteType = function(categoryId: number, type: BlockType) {
        setDeleteConfirm({ type: 'type', catId: categoryId, typeId: type.id, name: type.name });
    };

    var executeDelete = function() {
        if (!deleteConfirm) return;
        if (deleteConfirm.type === 'category') {
            axiosCaller('DELETE', API_BASE + '/' + deleteConfirm.catId).then(function() {
                toast.success(t('admin.installationBlockCategories.toastCatDeleted'));
                if (expandedId === deleteConfirm.catId) setExpandedId(null);
                setDeleteConfirm(null);
                loadCategories();
            }).catch(function() { toast.error(t('admin.toast.somethingWentWrong')); setDeleteConfirm(null); });
        } else {
            axiosCaller('DELETE', API_BASE + '/' + deleteConfirm.catId + '/types/' + deleteConfirm.typeId).then(function() {
                toast.success(t('admin.installationBlockCategories.toastTypeDeleted'));
                setDeleteConfirm(null);
                loadCategories();
            }).catch(function(err: any) { toast.error(err?.response?.data?.message || t('admin.toast.somethingWentWrong')); setDeleteConfirm(null); });
        }
    };

    var toggleExpand = function(catId: number) {
        setExpandedId(expandedId === catId ? null : catId);
        setNewTypeName('');
        setEditingTypeId(null);
    };

    return (
        <div style={{ padding: 20, width: '100%' }}>
            <div className="os-page">
                <div className="os-page-header">
                    <div>
                        <h1 className="os-page-title"><FormattedMessage id="admin.installationBlockCategories.pageTitle" defaultMessage="Kategoritë e bllokimit" /></h1>
                        <p className="os-page-subtitle"><FormattedMessage id="admin.installationBlockCategories.pageSubtitle" defaultMessage="Menaxhoni kategoritë dhe llojet që përdoren kur bllokohet një instalim" /></p>
                    </div>
                </div>

                <div className="os-card">
                    <div className="os-card-header">
                        <div>
                            <h2><FormattedMessage id="admin.installationBlockCategories.cardTitle" defaultMessage="Kategoritë dhe llojet" /></h2>
                            <p className="os-card-desc"><FormattedMessage id="admin.installationBlockCategories.cardDesc" defaultMessage="Klikoni mbi një kategori për të parë dhe menaxhuar llojet e saj" /></p>
                        </div>
                    </div>
                    <div className="os-card-body" style={{ padding: 0 }}>
                        {loading ? (
                            <p className="os-empty-text" style={{ padding: 20 }}><FormattedMessage id="common.loading" defaultMessage="Duke ngarkuar..." /></p>
                        ) : (
                            <>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    {categories.map(function(cat) {
                                        var isExpanded = expandedId === cat.id;
                                        var isEditing = editingCatId === cat.id;
                                        var hasTypes = !cat.is_protected;
                                        var colors = getCatColor(cat.name);
                                        var icon = categoryIcons[cat.name] || defaultIcon;

                                        return (
                                            <div key={cat.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                {/* Category row */}
                                                <div
                                                    style={{
                                                        display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12,
                                                        cursor: hasTypes ? 'pointer' : 'default',
                                                        background: isExpanded ? '#fafbfc' : '#fff',
                                                        borderLeft: '3px solid ' + colors.border,
                                                        transition: 'all 0.15s',
                                                    }}
                                                    onClick={function() { if (hasTypes && !isEditing) toggleExpand(cat.id); }}
                                                    onMouseEnter={function(e: any) { if (!isExpanded) e.currentTarget.style.background = '#fafbfc'; }}
                                                    onMouseLeave={function(e: any) { if (!isExpanded) e.currentTarget.style.background = '#fff'; }}
                                                >
                                                    {/* Icon circle */}
                                                    <div style={{
                                                        width: 32, height: 32, borderRadius: 8, background: colors.bg,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: colors.icon, flexShrink: 0,
                                                    }}>
                                                        {icon}
                                                    </div>

                                                    {/* Name or edit */}
                                                    {isEditing ? (
                                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }} onClick={function(e) { e.stopPropagation(); }}>
                                                            <input type="text" value={editingCatName}
                                                                onChange={function(e) { setEditingCatName(e.target.value); }}
                                                                onKeyDown={function(e) { if (e.key === 'Enter') saveCatEdit(); if (e.key === 'Escape') setEditingCatId(null); }}
                                                                autoFocus style={inputStyle} />
                                                            <button onClick={saveCatEdit} style={{ ...btnStyle(''), color: '#22c55e' }}>
                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                                            </button>
                                                            <button onClick={function() { setEditingCatId(null); }} style={{ ...btnStyle(''), color: '#94a3b8' }}>
                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{cat.name}</span>
                                                            {cat.is_protected && (
                                                                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#f1f5f9', color: '#94a3b8', fontWeight: 500 }}><FormattedMessage id="admin.installationBlockCategories.protected" defaultMessage="e mbrojtur" /></span>
                                                            )}
                                                            <span style={{ marginLeft: 'auto' }} />
                                                            {hasTypes && (
                                                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: colors.bg, color: colors.icon, fontWeight: 600 }}>
                                                                    {cat.types.length}
                                                                </span>
                                                            )}
                                                            {/* Chevron */}
                                                            {hasTypes && (
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
                                                                    style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}>
                                                                    <polyline points="9 18 15 12 9 6" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Actions */}
                                                    {!isEditing && (
                                                        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={function(e) { e.stopPropagation(); }}>
                                                            <button
                                                                onClick={function() { setEditingCatId(cat.id); setEditingCatName(cat.name); }}
                                                                style={{ ...btnStyle(''), color: '#94a3b8' }}
                                                                onMouseEnter={function(e: any) { e.currentTarget.style.background = '#eef3f9'; e.currentTarget.style.color = '#1e3a5f'; }}
                                                                onMouseLeave={function(e: any) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#94a3b8'; }}
                                                                title={fm('common.action.edit', 'Ndrysho')}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                            </button>
                                                            <button
                                                                onClick={function() { setTranslatingCategory(cat); }}
                                                                style={{ ...btnStyle(''), color: '#94a3b8' }}
                                                                onMouseEnter={function(e: any) { e.currentTarget.style.background = '#eef3f9'; e.currentTarget.style.color = '#1e3a5f'; }}
                                                                onMouseLeave={function(e: any) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#94a3b8'; }}
                                                                title={fm('common.action.translations', 'Përkthime')}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <circle cx="12" cy="12" r="10"/>
                                                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                                                </svg>
                                                            </button>
                                                            {!cat.is_protected && (
                                                                <button
                                                                    onClick={function() { deleteCategory(cat); }}
                                                                    style={{ ...btnStyle(''), color: '#94a3b8' }}
                                                                    onMouseEnter={function(e: any) { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                                                                    onMouseLeave={function(e: any) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#94a3b8'; }}
                                                                    title={fm('common.action.delete', 'Fshi')}>
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Expanded types */}
                                                {isExpanded && hasTypes && (
                                                    <div style={{ padding: '8px 16px 14px 53px', background: '#fafbfc', borderLeft: '3px solid ' + colors.border }}>
                                                        {cat.types.length === 0 && (
                                                            <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 8px', fontStyle: 'italic' }}><FormattedMessage id="admin.installationBlockCategories.noTypes" defaultMessage="Nuk ka lloje. Shtoni llojin e parë." /></p>
                                                        )}
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: cat.types.length > 0 ? 10 : 0 }}>
                                                            {cat.types.map(function(type) {
                                                                var isTypeEditing = editingTypeId === type.id;
                                                                if (isTypeEditing) {
                                                                    return (
                                                                        <div key={type.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                            <input type="text" value={editingTypeName}
                                                                                onChange={function(e) { setEditingTypeName(e.target.value); }}
                                                                                onKeyDown={function(e) { if (e.key === 'Enter') saveTypeEdit(cat.id); if (e.key === 'Escape') setEditingTypeId(null); }}
                                                                                autoFocus
                                                                                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid ' + colors.border, fontSize: 12, outline: 'none', width: 160 }} />
                                                                            <button onClick={function() { saveTypeEdit(cat.id); }} style={{ ...btnStyle(''), color: '#22c55e', padding: 3 }}>
                                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                                                            </button>
                                                                            <button onClick={function() { setEditingTypeId(null); }} style={{ ...btnStyle(''), color: '#94a3b8', padding: 3 }}>
                                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                                            </button>
                                                                        </div>
                                                                    );
                                                                }
                                                                return (
                                                                    <div key={type.id} style={{
                                                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                                                        padding: '5px 10px', borderRadius: 8,
                                                                        background: colors.bg, border: '1px solid ' + colors.border + '22',
                                                                        fontSize: 12, color: '#334155', fontWeight: 500,
                                                                        transition: 'all 0.15s',
                                                                    }}>
                                                                        <span>{type.name}</span>
                                                                        <button
                                                                            onClick={function() { setEditingTypeId(type.id); setEditingTypeName(type.name); }}
                                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 1, display: 'flex' }}
                                                                            onMouseEnter={function(e: any) { e.currentTarget.style.color = colors.icon; }}
                                                                            onMouseLeave={function(e: any) { e.currentTarget.style.color = '#94a3b8'; }}
                                                                            title={fm('common.action.edit', 'Ndrysho')}>
                                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                                        </button>
                                                                        <button
                                                                            onClick={function() { deleteType(cat.id, type); }}
                                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 1, display: 'flex' }}
                                                                            onMouseEnter={function(e: any) { e.currentTarget.style.color = '#ef4444'; }}
                                                                            onMouseLeave={function(e: any) { e.currentTarget.style.color = '#94a3b8'; }}
                                                                            title={fm('common.action.delete', 'Fshi')}>
                                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Add type */}
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <input type="text" value={newTypeName}
                                                                onChange={function(e) { setNewTypeName(e.target.value); }}
                                                                onKeyDown={function(e) { if (e.key === 'Enter') addType(cat.id); }}
                                                                placeholder={fm('admin.installationBlockCategories.placeholderType', 'Shto lloj të ri...')}
                                                                style={{ ...inputStyle, fontSize: 12, padding: '5px 10px' }} />
                                                            <button onClick={function() { addType(cat.id); }} disabled={!newTypeName.trim()}
                                                                style={{
                                                                    padding: '5px 12px', borderRadius: 6, border: 'none',
                                                                    background: newTypeName.trim() ? colors.border : '#e2e8f0',
                                                                    color: newTypeName.trim() ? '#fff' : '#94a3b8',
                                                                    fontSize: 11, fontWeight: 600, cursor: newTypeName.trim() ? 'pointer' : 'default',
                                                                    display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' as any,
                                                                }}>
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                                                <FormattedMessage id="admin.installationBlockCategories.btnAdd" defaultMessage="Shto" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Add category */}
                                <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input type="text" value={newCatName}
                                        onChange={function(e) { setNewCatName(e.target.value); }}
                                        onKeyDown={function(e) { if (e.key === 'Enter') addCategory(); }}
                                        placeholder={fm('admin.installationBlockCategories.placeholderCat', 'Emri i kategorisë së re...')}
                                        style={inputStyle} />
                                    <button onClick={addCategory} disabled={!newCatName.trim()}
                                        style={{
                                            padding: '7px 16px', borderRadius: 6, border: 'none',
                                            background: newCatName.trim() ? '#1e3a5f' : '#e2e8f0',
                                            color: newCatName.trim() ? '#fff' : '#94a3b8',
                                            fontSize: 12, fontWeight: 600, cursor: newCatName.trim() ? 'pointer' : 'default',
                                            display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' as any,
                                        }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        <FormattedMessage id="admin.installationBlockCategories.btnAddCat" defaultMessage="Shto kategori" />
                                    </button>
                                </div>

                                {categories.length === 0 && (
                                    <p className="os-empty-text" style={{ padding: 20 }}><FormattedMessage id="admin.installationBlockCategories.empty" defaultMessage="Nuk ka kategori. Shtoni kategorinë e parë." /></p>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <MikaConfirmModal
                    isOpen={!!deleteConfirm}
                    onClose={function() { setDeleteConfirm(null); }}
                    onConfirm={executeDelete}
                    title={deleteConfirm?.type === 'category'
                        ? fm('admin.installationBlockCategories.deleteCatTitle', 'Fshi kategorinë')
                        : fm('admin.installationBlockCategories.deleteTypeTitle', 'Fshi llojin')}
                    message={intl.formatMessage(
                        { id: 'admin.installationBlockCategories.deleteConfirm', defaultMessage: 'A jeni të sigurt që doni të fshini {name}?' },
                        { name: '"' + (deleteConfirm?.name || '') + '"' }
                    )}
                    confirmLabel={fm('common.action.delete', 'Fshi')}
                    confirmColor="danger"
                />

                <MikaTranslationsModal
                    isOpen={!!translatingCategory}
                    onClose={function() { setTranslatingCategory(null); }}
                    title={fm('admin.installationBlockCategories.translationsTitle', 'Përkthime: {name}').replace('{name}', translatingCategory ? translatingCategory.name : '')}
                    initialTranslations={translatingCategory ? (translatingCategory.name_translations || {}) : {}}
                    onSave={saveTranslations}
                />
            </div>
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(InstallationBlockCategories as any)));
